
import { action, observable } from 'mobx';
import * as Models from 'Models/Entities';
import {
	attribute,
	entity,
	IModelAttributes,
	Model,
	ReferencePath,
} from 'Models/Model';
import UserAlaresReport from 'Models/Security/Acl/UserAlaresReport';
import VisitorsAlaresReport from 'Models/Security/Acl/VisitorsAlaresReport';
import { IAcl } from 'Models/Security/IAcl';
import * as AttrUtils from 'Util/AttributeUtils';
import {
	getCreatedModifiedCrudOptions,
	makeEnumFetchFunction,
	makeFetchOneToManyFunc,
} from 'Util/EntityUtils';
import * as Validators from 'Validators';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { CRUD } from '../CRUDOptions';
import * as Enums from '../Enums';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';

import { AlaresReport } from 'Models/Interfaces/AlaresReport';
import moment, { Moment } from 'moment';
import { DateUtil } from 'Util/DateUtil';

export interface IAlaresReportEntityAttributes extends IModelAttributes {
	insolvencyRiskRatingValue: number;
	lastNotification: Date | null;
	adverseAlerts: number;
	insolvencyRiskRating: Enums.riskScore;
	processed: boolean;
	jsonreport: string;
	reportID: number;
	pdfId?: string;

	monitoredId: string;
	monitored: Models.MonitoredEntity | Models.IMonitoredEntityAttributes;
}

@entity('AlaresReportEntity', 'Alares Report')
export default class AlaresReportEntity extends Model
	implements IAlaresReportEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserAlaresReport(),
		new VisitorsAlaresReport(),
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The raw decimal value of the insolvency risk rating
	 */
	@Validators.Numeric()
	@observable
	@attribute<AlaresReportEntity, number>()
	@CRUD({
		name: 'Insolvency Risk Rating Value',
		displayType: 'textfield',
		order: 10,
		headerColumn: false,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public insolvencyRiskRatingValue: number;

	@observable
	@attribute<AlaresReportEntity, Date>()
	@CRUD({
		name: 'Last Notification',
		displayType: 'datetimepicker',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseDate,
	})
	public lastNotification: Date | null;

	@Validators.Integer()
	@observable
	@attribute<AlaresReportEntity, number>()
	@CRUD({
		name: 'Adverse Alerts',
		displayType: 'textfield',
		order: 30,
		headerColumn: false,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public adverseAlerts: number;

	@observable
	@attribute<AlaresReportEntity, Enums.riskScore>()
	@CRUD({
		name: 'Insolvency Risk Rating',
		displayType: 'enum-combobox',
		order: 40,
		headerColumn: false,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.riskScoreOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.riskScoreOptions),
		displayFunction: (attr: Enums.riskScore) => Enums.riskScoreOptions[attr],
	})
	public insolvencyRiskRating: Enums.riskScore;




	/**
	 * Whether or not the status has been processed
	 */
	@observable
	@attribute<AlaresReportEntity, boolean>()
	@CRUD({
		name: 'Processed',
		displayType: 'checkbox',
		order: 60,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public processed: boolean;

	/**
	 * Alares report in json
	 */
	@observable
	@attribute<AlaresReportEntity, string>()
	@CRUD({
		name: 'JsonReport',
		displayType: 'textarea',
		order: 70,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public jsonreport: string;

	@observable
	@attribute<AlaresReportEntity, string>()
	public pdfId?: string;

	@Validators.Integer()
	@Validators.Unique()
	@observable
	@attribute<AlaresReportEntity, number>()
	@CRUD({
		name: 'Report ID',
		displayType: 'textfield',
		order: 80,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public reportID: number;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({

		name: 'Monitored',
		displayType: 'reference-combobox',
		order: 90,
		headerColumn: true,
		referenceTypeFunc: () => Models.MonitoredEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'monitoreds',
			oppositeEntity: () => Models.MonitoredEntity,
		}),
	})
	public monitoredId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public monitored: Models.MonitoredEntity;

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IAlaresReportEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IAlaresReportEntityAttributes>) {

		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.insolvencyRiskRatingValue !== undefined) {
				this.insolvencyRiskRatingValue = attributes.insolvencyRiskRatingValue;
			}
			if (attributes.lastNotification !== undefined) {
				if (attributes.lastNotification === null) {
					this.lastNotification = attributes.lastNotification;
				} else {
					this.lastNotification = moment(attributes.lastNotification).toDate();
				}
			}
			if (attributes.adverseAlerts !== undefined) {
				this.adverseAlerts = attributes.adverseAlerts;
			}
			if (attributes.insolvencyRiskRating !== undefined) {
				this.insolvencyRiskRating = attributes.insolvencyRiskRating;
			}

			if (attributes.processed !== undefined) {
				this.processed = attributes.processed;
			}
			if (attributes.jsonreport !== undefined) {
				this.jsonreport = attributes.jsonreport;
			}
			if (attributes.reportID !== undefined) {
				this.reportID = attributes.reportID;
			}
			if (attributes.pdfId !== undefined) {
				this.pdfId = attributes.pdfId;
			}
			if (attributes.monitoredId !== undefined) {
				this.monitoredId = attributes.monitoredId;
			}
			if (attributes.monitored !== undefined) {
				if (attributes.monitored === null) {
					this.monitored = attributes.monitored;
				} else if (attributes.monitored instanceof Models.MonitoredEntity) {
					this.monitored = attributes.monitored;
					this.monitoredId = attributes.monitored.id;
				} else {
					this.monitored = new Models.MonitoredEntity(attributes.monitored);
					this.monitoredId = this.monitored.id;
				}
			}


		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */

	public defaultExpands = `
		monitored {
			${Models.MonitoredEntity.getAttributes().join('\n')}
		}
	`;
	/**
		 * The save method that is called from the admin CRUD components.
		 */

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'monitored',
						],
					},
				],
			},
		);
	}

	/**
		 * Returns the string representation of this entity to display on the UI.
		 */
	public getDisplayName() {
		return this.id;
	}

	public getExtractedDate(): Moment | null {

		try {
			// Some json report are partially saved because its too big so the content is corrupt and cant be deserialized
			const alaresReportContent = JSON.parse(this.jsonreport ?? "") as AlaresReport | null;
			if (alaresReportContent == null || alaresReportContent.created_at == null) return null;
			return moment(alaresReportContent.created_at);
		} catch (e) {
			return null;
		}
	}

	public getDaysPassedWithoutReportText = (): string => {
		const extractedDate = this.getExtractedDate()
		const showDaysPassedWithoutReport = !(extractedDate != null || this.created == null);

		// Show empty value if we have extracted.
		if (!showDaysPassedWithoutReport) return "-";

		// Otherwise show how long its been
		const now = moment();
		const past = moment(this.created);
		const days = now.diff(past, 'days'); // Get total days difference
		const hours = now.diff(past.add(days, 'days'), 'hours'); // Get remaining hours

		return `${days} days ${hours} hours`;
	}
}

// AlaresReportEntity records before this date had ReportId cleared after JSON content was stored,
// which means the ReportId needed to request the PDF is no longer available.
export const ALARES_PDF_SUPPORTED_FROM = new Date('2026-06-10');

export type AlaresReportPdfDto = Pick<AlaresReportEntity, 'processed'> & Pick<IModelAttributes, 'created'> & Partial<Pick<AlaresReportEntity, 'reportID'>>;

export function getAlaresPdfDisabledReason(report: AlaresReportPdfDto): string | undefined {
	if (report.created && new Date(report.created) < ALARES_PDF_SUPPORTED_FROM) return `PDF download is not available for reports generated before ${DateUtil.ToFriendlyDateString(ALARES_PDF_SUPPORTED_FROM)}.`;
	if (!report.reportID) return "No Alares report is available for this entity.";
	if (!report.processed) return "The Alares report is still processing.";
	return undefined;
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(AlaresReportEntity.prototype, 'created');
CRUD(modifiedAttr)(AlaresReportEntity.prototype, 'modified');

